import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';

const freeFeatures = [
    'Unlimited badge creation', 'Issue badges to any number of earners',
    'Open Badges 2.0+ compliant exports', 'Public badge discovery page',
    'Digital portfolio for earners', 'Badge verification for third parties',
    'Basic analytics', 'API access',
];

const premiumFeatures = [
    'Advanced analytics & reporting', 'Custom branding on issuer pages',
    'Priority support', 'Bulk badge issuance',
    'LMS integration connectors', 'White-label options',
    'SSO / Enterprise login', 'SLA guarantees',
];

export default function WhyFreePage() {
    return (
        <>
            <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 10, textAlign: 'center' }}>
                <Container maxWidth="md">
                    <Typography variant="h3" fontWeight={700} gutterBottom>Why Is TC Everywhere Free?</Typography>
                    <Typography variant="h6" sx={{ opacity: 0.85 }}>A free tool for teachers, lifelong learners, and non-profits — forever.</Typography>
                </Container>
            </Box>

            <Box component="section" sx={{ py: 8 }}>
                <Container maxWidth="lg">
                    <Box sx={{ maxWidth: 700, mx: 'auto' }}>
                        <Typography variant="h4" fontWeight={700} gutterBottom>Our Promise</Typography>
                        <Typography color="text.secondary" paragraph>
                            TC Everywhere began with a simple goal: to make recognizing learning achievements
                            accessible to everyone. That commitment remains unchanged. Teachers and non-profit
                            organizations will always have free access to create and share badges with their
                            communities.
                        </Typography>
                        <Typography color="text.secondary" paragraph>
                            We believe that digital credentials should not be locked behind a paywall. The ability
                            to recognize and showcase achievement is a fundamental part of education, professional
                            development, and personal growth — and that shouldn't cost anything.
                        </Typography>
                        <Typography color="text.secondary">
                            We sustain this commitment through premium features designed for organizations that
                            need more. That's the deal: organizations that can afford to pay help keep it free for everyone else.
                        </Typography>
                    </Box>
                </Container>
            </Box>

            <Box component="section" sx={{ py: 8, bgcolor: 'background.paper' }}>
                <Container maxWidth="lg">
                    <Typography variant="h4" fontWeight={700} textAlign="center" mb={6}>What's Included</Typography>
                    <Grid container spacing={4} justifyContent="center">
                        <Grid item xs={12} md={5}>
                            <Card sx={{ height: '100%' }}>
                                <Box sx={{ bgcolor: 'success.main', color: 'white', textAlign: 'center', py: 2 }}>
                                    <Typography variant="h6" fontWeight={700}>
                                        <Box component="i" className="bi bi-gift" sx={{ mr: 1 }} />Free — Always
                                    </Typography>
                                    <Typography variant="caption" sx={{ opacity: 0.8 }}>For individuals, educators &amp; non-profits</Typography>
                                </Box>
                                <CardContent sx={{ p: 3 }}>
                                    {freeFeatures.map(f => (
                                        <Box key={f} display="flex" alignItems="center" gap={1} mb={1.5}>
                                            <Box component="i" className="bi bi-check-circle-fill" sx={{ color: 'success.main', flexShrink: 0 }} />
                                            <Typography variant="body2" color="text.secondary">{f}</Typography>
                                        </Box>
                                    ))}
                                    <Button component={Link} to="/register" variant="contained" color="success" fullWidth sx={{ mt: 2 }}>
                                        Get Started Free
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <Card sx={{ height: '100%' }}>
                                <Box sx={{ bgcolor: 'primary.main', color: 'white', textAlign: 'center', py: 2 }}>
                                    <Typography variant="h6" fontWeight={700}>
                                        <Box component="i" className="bi bi-star" sx={{ mr: 1 }} />Premium
                                    </Typography>
                                    <Typography variant="caption" sx={{ opacity: 0.8 }}>For organizations that need more</Typography>
                                </Box>
                                <CardContent sx={{ p: 3 }}>
                                    {premiumFeatures.map(f => (
                                        <Box key={f} display="flex" alignItems="center" gap={1} mb={1.5}>
                                            <Box component="i" className="bi bi-star-fill" sx={{ color: 'accent.main', flexShrink: 0 }} />
                                            <Typography variant="body2" color="text.secondary">{f}</Typography>
                                        </Box>
                                    ))}
                                    <Button component={Link} to="/contact" variant="contained" fullWidth sx={{ mt: 2 }}>
                                        Contact Us
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            <Box component="section" sx={{ py: 8 }}>
                <Container maxWidth="md" sx={{ textAlign: 'center' }}>
                    <Box component="i" className="bi bi-arrow-repeat" sx={{ fontSize: '3rem', color: 'primary.main', mb: 2, display: 'block' }} />
                    <Typography variant="h4" fontWeight={700} gutterBottom>Sustainable Growth Through Premium Features</Typography>
                    <Typography color="text.secondary" paragraph>
                        Organizations that need advanced capabilities pay for those features. That revenue
                        directly funds the infrastructure, development, and support that keeps the
                        platform free for everyone else. It's a model we're proud of.
                    </Typography>
                    <Typography color="text.secondary" fontWeight={600}>Thank you for being part of this journey.</Typography>
                </Container>
            </Box>
        </>
    );
}
